import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import LoadingButton from "./LoadingButton";
import { Checkbox } from "./ui/checkbox";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { useApplicator } from "../context/ApplicatorContext";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const RegisterApplicator = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const navigate = useNavigate();

  const { setApplicatorId, setCompanyName } = useApplicator();

  const [isLoading, setIsLoading] = useState(false);
  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [privacyPolicyViewed, setPrivacyPolicyViewed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [privacyColor, setPrivacyColor] = useState("#00B5ED");
  const [toastList, setToastList] = useState([]);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [termsAndConditionsViewed, setTermsAndConditionsViewed] =
    useState(false);
  // eslint-disable-next-line no-unused-vars
  const [userSession, setUserSession] = useState(null); // State to hold user session data

  const [termsColor, setTermsColor] = useState("#00B5ED");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    companyLocation: "",
    jobTitle: "",
    reasonForApplication: "",
  });

  //   const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const passwordsMatch =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword;

  const showPasswordMismatch =
    formData.confirmPassword.length > 0 &&
    formData.password !== formData.confirmPassword;

  const passwordRequirements = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /\d/.test(formData.password),
    specialChar: /[@$!%*?&#]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  useEffect(() => {
    // Enabling checkbox when both documents are viewed
    if (privacyPolicyViewed && termsAndConditionsViewed) {
      setIsCheckboxEnabled(true);
      setIsCheckboxChecked(true);
    }
  }, [privacyPolicyViewed, termsAndConditionsViewed]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 30000);

    // Check if password is strong before submission
    if (!isPasswordValid) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description:
            "Weak password! Must have 8+ characters, uppercase, lowercase, number, and special character.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Passwords do not match!",
          variant: "destructive",
        },
      ]);

      setIsLoading(false);
      // setIsSignUpDisabled(false);
      // setIsRequestDisabled(true); // Disable Request button
      return false;
    }

    const dataToSubmit = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      companyName: formData.companyName,
      companyLocation: formData.companyLocation,
      jobTitle: formData.jobTitle,
      reasonForApplication: formData.reasonForApplication,
    };

    try {
      const response = await fetch(`${API_URL}/api/applicator/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
        },
        body: JSON.stringify(dataToSubmit),
      });

      if (response.ok) {
        // Extract the response JSON to access the error message
        const responseData = await response.json();
        const applicatorData = responseData.data.savedApplicator;

        // Store in Context
        setApplicatorId(applicatorData._id);
        setCompanyName(applicatorData.companyName);

        // persist in localStorage
        localStorage.setItem(
          "applicatorCompanyName",
          applicatorData.companyName,
        );

        // setToastList((prev) => [
        //   ...prev,
        //   {
        //     id: Date.now(),
        //     title: "SUCCESS!",
        //     description:
        //       responseData.data?.message ||
        //       "Registration Successful! We'll send an email to you with updates on the status of your registration soon.",
        //     variant: "default",
        //   },
        // ]);

        navigate("/pendingapproval");
      } else {
        const responseData = await response.json();

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message ||
              "An error occurred while processing your registration. Please try again.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      console.error("Error:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handlePrivacyPolicyClick = (url) => {
    window.open(url, "_blank");
    setPrivacyPolicyViewed(true);
    setPrivacyColor("#22c55e");
  };

  const handleTermsAndConditionsClick = (url) => {
    window.open(url, "_blank");
    setTermsAndConditionsViewed(true);
    setTermsColor("#22c55e");
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="tw-flex tw-flex-col tw-min-h-screen">
      {/* Left side (visible as blue only on desktop) */}
      {/* <div className="tw-absolute -tw-inset-24 tw-z-0 md:tw-w-[54%] tw-hidden tw-min-h-full md:tw-flex md:tw-flex-1 tw-bg-[#f1fcff]"></div>
      <div className="tw-hidden md:tw-flex md:tw-w-[50%] tw-relative tw-min-h-screen">
        <div className="tw-flex md:tw-w-[70%] tw-justify-center tw-items-center tw-h-full tw-absolute tw-inset-0 tw-z-0">
          <img
            src="/authimg.png"
            alt="Sign In Illustration"
            className="md:tw-h-[40%]"
          />
        </div>
      </div> */}

      {/* Right side (white background visible on both desktop and mobile) */}
      <div className="tw-flex tw-flex-col tw-min-h-screen tw-items-center tw-w-full tw-justify-center">
        <div className="md:tw-w-[70%] tw-flex tw-flex-col tw-items-center tw-justify-center">
          <Card className="tw-shadow-none tw-border-none tw-mb-0 tw-bg-transparent tw-max-w-auto ">
            <CardHeader className="tw-px-0">
              <CardTitle className="tw-text-xl md:tw-text-3xl h-sm:tw-text-2xl">
                Applicator Registration
              </CardTitle>
              <CardDescription>
                {/* <p>Enter your details to register as an applicator</p> */}

                <ul className="tw-my-2 tw-space-y-2 tw-font-medium tw-text-black">
                  <li>
                    - Each applicator user must have a unique ID number to
                    access the portal.
                  </li>

                  <li>
                    - Please complete and submit the Applicator Registration
                    form below. Upon approval, you will receive an email
                    containing your Unique Applicator ID Number.
                  </li>

                  <li>
                    - Do not disclose this unique ID number to anyone. If other
                    individuals within your organization require portal access,
                    please direct them to the website to complete the
                    application form.
                  </li>

                  <li>
                    - Approval may take up to 24 hours. If immediate approval is
                    needed, please send an email to contact@sippamericas.com
                    after submitting the application.
                  </li>

                  <li>
                    - Upon approval, you will receive an email containing your
                    Unique Applicator ID Number. This number is necessary for
                    accessing the Applicator Portal. The email will include
                    detailed instructions on how to log in.
                  </li>

                  <li>
                    If you need access but are not approved, email
                    contact@sippamericas.com.
                  </li>
                </ul>
              </CardDescription>
            </CardHeader>
            <CardContent className="tw-px-0">
              <form onSubmit={handleSubmit}>
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-gap-6">
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="firstName">
                      First Name
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="firstName"
                      type="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Enter first name"
                      required
                    />
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="lastName">
                      Last Name
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="lastName"
                      type="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Enter last name"
                      required
                    />
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="email">
                      Email
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="password">
                      Password
                    </Label>
                    <div className="tw-relative">
                      <Input
                        className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm tw-pr-10"
                        id="password"
                        value={formData.password}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        required
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={() => setIsPasswordFocused(false)}
                        pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&]).{8,}"
                        title="Password must contain at least 8 characters, including uppercase, lowercase, numbers, and special characters."
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="tw-absolute tw-right-3 tw-top-2 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
                        aria-label="Toggle password visibility"
                        tabIndex="-1"
                      >
                        {showPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                    {(isPasswordFocused || !isPasswordValid) && (
                      <div className="tw-text-xs tw-mt-2 tw-space-y-1">
                        <p
                          className={
                            passwordRequirements.length
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.length ? "✅" : "⬜"} At least 8
                          characters
                        </p>

                        <p
                          className={
                            passwordRequirements.uppercase
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.uppercase ? "✅" : "⬜"} One
                          uppercase letter (A–Z)
                        </p>

                        <p
                          className={
                            passwordRequirements.lowercase
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.lowercase ? "✅" : "⬜"} One
                          lowercase letter (a–z)
                        </p>

                        <p
                          className={
                            passwordRequirements.number
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.number ? "✅" : "⬜"} One number
                          (0–9)
                        </p>

                        <p
                          className={
                            passwordRequirements.specialChar
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.specialChar ? "✅" : "⬜"} One
                          special character (!@#$%)
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="confirmPassword">
                      Confirm Password
                    </Label>
                    <div className="tw-relative">
                      <Input
                        className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm tw-pr-10"
                        id="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Enter password"
                        required
                        disabled={!isPasswordValid}
                      />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className="tw-absolute tw-right-3 tw-top-2 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
                        aria-label="Toggle password visibility"
                        tabIndex="-1"
                      >
                        {showConfirmPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </div>

                    {passwordsMatch && (
                      <p className="tw-text-xs tw-text-green-600 tw-font-medium">
                        ✅ Password match
                      </p>
                    )}

                    {showPasswordMismatch && (
                      <p className="tw-text-xs tw-text-red-500 tw-font-medium">
                        ❌ Password does not match
                      </p>
                    )}
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="companyName">
                      Company Name
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="companyName"
                      type="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Enter company name"
                      required
                    />
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="companyName">
                      Company Location
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="companyLocation"
                      type="companyLocation"
                      value={formData.companyLocation}
                      onChange={handleChange}
                      placeholder="Enter company location"
                      required
                    />
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="jobTitle">
                      Job Title
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="jobTitle"
                      type="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      placeholder="Enter job title"
                      required
                    />
                  </div>

                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="jobTitle">
                      State Reason for Applicator Portal Access
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="reasonForApplication"
                      type="reasonForApplication"
                      value={formData.reasonForApplication}
                      onChange={handleChange}
                      placeholder="Enter reason"
                      required
                    />
                  </div>
                </div>

                <p className="tw-text-center tw-text-[10px] tw-mt-3 tw-italic tw-text-gray-500">
                  Click on both terms to get the checkbox ticked automatically
                </p>

                <div className="tw-flex tw-items-center tw-space-x-2 tw-justify-center tw-mt-1">
                  <Checkbox
                    id="termsCheckbox"
                    disabled={!isCheckboxEnabled}
                    checked={isCheckboxChecked}
                    onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                    required
                  />
                  <span className="tw-text-xs md:tw-text-sm">
                    I agree to the terms of the{" "}
                    <span
                      type="text"
                      onClick={() =>
                        handlePrivacyPolicyClick("/privacypolicy.pdf")
                      }
                      className="tw-cursor-pointer tw-font-medium hover:tw-underline"
                      style={{ color: privacyColor }}
                    >
                      Privacy Policy
                    </span>{" "}
                    and the{" "}
                    <span
                      type="text"
                      onClick={() =>
                        handleTermsAndConditionsClick("/terms.pdf")
                      }
                      className="tw-cursor-pointer tw-font-medium hover:tw-underline"
                      style={{ color: termsColor }}
                    >
                      Site Use Terms and Conditions
                    </span>
                  </span>
                </div>

                {isLoading ? (
                  <LoadingButton />
                ) : (
                  <Button
                    disabled={!isCheckboxEnabled}
                    type="submit"
                    className="tw-w-full tw-font-semibold tw-mt-6 md:tw-mt-8 tw-text-sm"
                  >
                    SUBMIT
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
          <div className="tw-text-center tw-text-xs md:tw-text-sm">
            Already a registered applicator?{" "}
            <Link to="/signin">
              <span className="tw-text-[#00B5ED] tw-font-medium">Sign in</span>
            </Link>
          </div>
          <div className="tw-text-center tw-text-xs md:tw-text-sm tw-mb-6 tw-mt-2">
            Back to{" "}
            <Link to="/">
              <span className="tw-text-[#00B5ED] tw-font-medium">Home</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default RegisterApplicator;
